// Precompiled regexes per separator group — compiled once at module load, reused on every call.
// Each regex encodes the exact token structure of its format family.

// DD.MM.YYYY [HH:mm[:ss]] — day/month can be 1 or 2 digits
const DOT_DATE_TIME_SS = /^(\d{1,2})\.(\d{1,2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
const DOT_DATE_TIME = /^(\d{1,2})\.(\d{1,2})\.(\d{4}) (\d{2}):(\d{2})$/;
const DOT_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

// MM/DD/YYYY [HH:mm[:ss]] and DD/MM/YYYY — ambiguous, both accepted
const SLASH_DATE_TIME_SS = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
const SLASH_DATE_TIME = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{2}):(\d{2})$/;
const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
// YYYY/MM/DD
const SLASH_YMD = /^(\d{4})\/(\d{2})\/(\d{2})$/;

// YYYY-MM-DD [HH:mm:ss]
const DASH_DATE_TIME_SS = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
const DASH_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// ISO: YYYY-MM-DDTHH:mm:ss[Z]
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;
const ISO_DATE_TIME_Z = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;

// Time only
const TIME_HMS = /^(\d{2}):(\d{2}):(\d{2})$/;
const TIME_HM = /^(\d{2}):(\d{2})$/;

// Days-in-month lookup (non-leap). Index 0 unused so month maps directly.
const DAYS_IN_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isValidDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  if (day < 1) return false;
  const maxDay = month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month];
  return day <= maxDay;
}

function isValidTime(hour: number, minute: number, second: number): boolean {
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 && second >= 0 && second <= 59;
}

// The dot/slash families accept 1- or 2-digit day and month, so the returned token must
// mirror the width that actually matched — otherwise the format would not round-trip
// through a strict moment(value, format, true) parse.
function dayToken(raw: string): string {
  return raw.length === 2 ? "DD" : "D";
}

function monthToken(raw: string): string {
  return raw.length === 2 ? "MM" : "M";
}

type MatchResult = RegExpExecArray | null;

/**
 * High-performance date format validator.
 *
 * Replaces moment.js-based validation that tried up to 23 moment(value, format, true)
 * calls per invocation (~150ms). This uses precompiled regexes grouped by separator
 * with early exit — typical call completes in <0.05ms.
 *
 * Supported formats:
 *   Dot:   DD.MM.YYYY, DD.MM.YYYY HH:mm, DD.MM.YYYY HH:mm:ss (D/M variants)
 *   Slash: MM/DD/YYYY, DD/MM/YYYY, YYYY/MM/DD (with optional HH:mm, HH:mm:ss)
 *   Dash:  YYYY-MM-DD, YYYY-MM-DD HH:mm:ss
 *   ISO:   YYYY-MM-DDTHH:mm:ss, YYYY-MM-DDTHH:mm:ssZ
 *   Time:  HH:mm, HH:mm:ss
 *
 * detectFormat returns the moment format token the value matched, so a caller can parse it
 * back with the SAME interpretation the validator used. Callers must never re-parse a
 * validated string with a format-less moment(value): that falls through to the JS Date
 * constructor, which reads "01.09.2026" month-first as 9 January (KOA-18396).
 */
export class DateFormatValidator {
  static isValid(value: unknown): boolean {
    return DateFormatValidator.detectFormat(value) !== null;
  }

  /**
   * Returns the moment format the value matched, or null when it is not a supported
   * date/time string. Year-first results (YYYY…) are machine formats; everything else is a
   * human day-first or time-only rendering that is already in its final presentation form.
   */
  static detectFormat(value: unknown): string | null {
    if (!value || typeof value !== "string") return null;

    const len = value.length;
    // Shortest: "HH:mm" (5), longest: "DD.MM.YYYY HH:mm:ss" / "YYYY-MM-DDTHH:mm:ssZ" (20)
    if (len < 5 || len > 20) return null;

    // All supported formats start with a digit
    const first = value.charCodeAt(0);
    if (first < 48 || first > 57) return null;

    // Route to the right format group based on separator
    if (value.charCodeAt(4) === 45 /* '-' */ && value.indexOf("T") !== -1) {
      return DateFormatValidator.tryISO(value);
    }
    if (value.indexOf(".") !== -1) {
      return DateFormatValidator.tryDot(value);
    }
    if (value.indexOf("/") !== -1) {
      return DateFormatValidator.trySlash(value);
    }
    if (value.charCodeAt(4) === 45 /* '-' */) {
      return DateFormatValidator.tryDash(value);
    }
    if (value.indexOf(":") !== -1) {
      return DateFormatValidator.tryTime(value);
    }

    return null;
  }

  /**
   * True when the format is year-first, i.e. a machine/interchange rendering
   * (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss, YYYY/MM/DD) rather than a human day-first one.
   */
  static isMachineFormat(format: string): boolean {
    return format.charCodeAt(0) === 89; /* 'Y' */
  }

  private static tryDot(value: string): string | null {
    let m: MatchResult;

    m = DOT_DATE_TIME_SS.exec(value);
    if (m) {
      if (!isValidDate(+m[3], +m[2], +m[1]) || !isValidTime(+m[4], +m[5], +m[6])) return null;
      return `${dayToken(m[1])}.${monthToken(m[2])}.YYYY HH:mm:ss`;
    }

    m = DOT_DATE_TIME.exec(value);
    if (m) {
      if (!isValidDate(+m[3], +m[2], +m[1]) || !isValidTime(+m[4], +m[5], 0)) return null;
      return `${dayToken(m[1])}.${monthToken(m[2])}.YYYY HH:mm`;
    }

    m = DOT_DATE.exec(value);
    if (m) {
      if (!isValidDate(+m[3], +m[2], +m[1])) return null;
      return `${dayToken(m[1])}.${monthToken(m[2])}.YYYY`;
    }

    return null;
  }

  /**
   * The DD/MM vs MM/DD slash family is genuinely ambiguous and both readings are accepted.
   * When a value is valid either way we report the day-first reading, matching the dot
   * family. Nothing re-serialises a slash value on the strength of that choice — these are
   * human formats and are passed through untouched — so the tie-break never rewrites data.
   */
  private static trySlash(value: string): string | null {
    let m: MatchResult;

    // YYYY/MM/DD
    m = SLASH_YMD.exec(value);
    if (m) {
      if (!isValidDate(+m[1], +m[2], +m[3])) return null;
      return "YYYY/MM/DD";
    }

    // MM/DD/YYYY or DD/MM/YYYY with time
    m = SLASH_DATE_TIME_SS.exec(value);
    if (m) {
      const timeValid: boolean = isValidTime(+m[4], +m[5], +m[6]);
      if (!timeValid) return null;
      return DateFormatValidator.slashDateFormat(m[1], m[2], +m[3], " HH:mm:ss");
    }

    m = SLASH_DATE_TIME.exec(value);
    if (m) {
      const timeValid: boolean = isValidTime(+m[4], +m[5], 0);
      if (!timeValid) return null;
      return DateFormatValidator.slashDateFormat(m[1], m[2], +m[3], " HH:mm");
    }

    m = SLASH_DATE.exec(value);
    if (m) {
      return DateFormatValidator.slashDateFormat(m[1], m[2], +m[3], "");
    }

    return null;
  }

  private static slashDateFormat(rawA: string, rawB: string, year: number, timeSuffix: string): string | null {
    const a: number = +rawA;
    const b: number = +rawB;

    // Day-first preferred, month-first as the fallback reading.
    if (isValidDate(year, b, a)) {
      return `${dayToken(rawA)}/${monthToken(rawB)}/YYYY${timeSuffix}`;
    }
    if (isValidDate(year, a, b)) {
      return `${monthToken(rawA)}/${dayToken(rawB)}/YYYY${timeSuffix}`;
    }
    return null;
  }

  private static tryDash(value: string): string | null {
    let m: MatchResult;

    m = DASH_DATE_TIME_SS.exec(value);
    if (m) {
      if (!isValidDate(+m[1], +m[2], +m[3]) || !isValidTime(+m[4], +m[5], +m[6])) return null;
      return "YYYY-MM-DD HH:mm:ss";
    }

    m = DASH_DATE.exec(value);
    if (m) {
      if (!isValidDate(+m[1], +m[2], +m[3])) return null;
      return "YYYY-MM-DD";
    }

    return null;
  }

  private static tryISO(value: string): string | null {
    let m: MatchResult;

    m = ISO_DATE_TIME_Z.exec(value);
    if (m) {
      if (!isValidDate(+m[1], +m[2], +m[3]) || !isValidTime(+m[4], +m[5], +m[6])) return null;
      return "YYYY-MM-DDTHH:mm:ss[Z]";
    }

    m = ISO_DATE_TIME.exec(value);
    if (m) {
      if (!isValidDate(+m[1], +m[2], +m[3]) || !isValidTime(+m[4], +m[5], +m[6])) return null;
      return "YYYY-MM-DDTHH:mm:ss";
    }

    return null;
  }

  private static tryTime(value: string): string | null {
    let m: MatchResult;

    m = TIME_HMS.exec(value);
    if (m) {
      if (!isValidTime(+m[1], +m[2], +m[3])) return null;
      return "HH:mm:ss";
    }

    m = TIME_HM.exec(value);
    if (m) {
      if (!isValidTime(+m[1], +m[2], 0)) return null;
      return "HH:mm";
    }

    return null;
  }
}
